import React from 'react';
import { X, Shield, Briefcase, HelpCircle } from 'lucide-react';
import { SYSTEM_ROLES, OWNER_ROLES } from '@/constants/roles';
import { useOrganization } from '@/contexts/OrganizationContext';
import { getOwnerRolesForOrganizationType } from '@/constants/organizationRoles';

interface RoleHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const RoleHelpModal: React.FC<RoleHelpModalProps> = ({ isOpen, onClose }) => {
  const { currentOrganization } = useOrganization();
  
  // Get organization-type-specific owner roles
  const organizationType = currentOrganization?.industryType || null;
  const availableOwnerRoles = getOwnerRolesForOrganizationType(organizationType);
  
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <HelpCircle className="w-6 h-6 text-blue-600" />
            <h3 className="text-xl font-semibold text-gray-800">Understanding User Roles</h3>
          </div>
          <button 
            onClick={onClose} 
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Introduction */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-900">
              <strong>Users can have multiple roles!</strong> There are two types of roles: 
              <strong> System Roles</strong> (control what you can do) and 
              <strong> Owner Roles</strong> (describe what you're responsible for).
            </p>
          </div>

          {/* System Roles Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-indigo-600" />
              <h4 className="text-lg font-semibold text-gray-800">System Roles - What You Can Do</h4>
            </div>
            <p className="text-sm text-gray-600">
              These roles control your access to features and permissions in the system.
            </p>
            
            <div className="grid gap-3 mt-3">
              {SYSTEM_ROLES.map((role) => {
                const descriptions: Record<string, { title: string; description: string; examples: string[] }> = {
                  'user': {
                    title: 'User',
                    description: 'Basic access with limited permissions',
                    examples: [
                      'View assigned locations and assets',
                      'Can only see and complete tasks assigned to their owner roles',
                      'Cannot see or complete tasks assigned to other owner roles',
                      'Cannot manage other users',
                      'Cannot access admin features'
                    ]
                  },
                  'admin': {
                    title: 'Admin',
                    description: 'Full administrative access to everything',
                    examples: [
                      'Manage all users and organizations',
                      'Access all features (subscriptions, settings)',
                      'Can see and complete/review ALL tasks regardless of assignment',
                      'Assign any role to users',
                      'Full system control'
                    ]
                  },
                  'manager': {
                    title: 'Manager',
                    description: 'Management access with some restrictions',
                    examples: [
                      'Manage users (except admins)',
                      'Assign locations to users',
                      'Can see and complete/review ALL tasks regardless of assignment',
                      'More permissions than regular users',
                      'Cannot assign admin role to others'
                    ]
                  }
                };

                const info = descriptions[role] || { title: role, description: '', examples: [] };

                return (
                  <div key={role} className="border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                          <span className="text-xs font-semibold text-indigo-700 capitalize">{role.charAt(0)}</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-800 capitalize mb-1">{info.title}</h5>
                        <p className="text-sm text-gray-600 mb-2">{info.description}</p>
                        <ul className="space-y-1">
                          {info.examples.map((example, idx) => (
                            <li key={idx} className="text-xs text-gray-600 flex items-start gap-2">
                              <span className="text-indigo-500 mt-1">•</span>
                              <span>{example}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Owner Roles Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <Briefcase className="w-5 h-5 text-green-600" />
              <h4 className="text-lg font-semibold text-gray-800">Owner Roles - What You're Responsible For</h4>
            </div>
            <p className="text-sm text-gray-600">
              These roles describe your business responsibilities and functional areas. They're used to assign compliance tasks and determine who is responsible for what.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              {availableOwnerRoles.map((role) => {
                const descriptions: Record<string, string> = {
                  // Bank roles
                  'Loan Officer': 'Processes and manages loan applications',
                  'Compliance Officer': 'Ensures regulatory compliance and adherence to banking standards',
                  'Credit Analyst': 'Analyzes creditworthiness and financial risk',
                  'Underwriter': 'Evaluates and approves loan applications',
                  'Loan Processor': 'Handles loan documentation and processing',
                  'Branch Manager': 'Manages branch operations and staff',
                  'Risk Manager': 'Assesses and manages financial and operational risks',
                  // Restaurant roles
                  'Kitchen Manager': 'Manages kitchen operations and compliance',
                  // Generic roles (used across multiple org types)
                  'Site Manager': 'Manages a specific site or location',
                  'Engineering Lead': 'Responsible for engineering-related compliance',
                  'Maintenance Supervisor': 'Oversees maintenance tasks and compliance',
                  'Owner/Admin': 'Business owner or administrator',
                  'HR Manager': 'Human resources responsibilities',
                  'Facilities Manager': 'Facilities management and compliance',
                  'Operations Manager': 'Operations oversight and management',
                  'Safety Officer': 'Safety and compliance oversight'
                };

                return (
                  <div key={role} className="border border-gray-200 rounded-lg p-3 bg-white hover:shadow-md transition-shadow">
                    <div className="flex items-start gap-2">
                      <div className="mt-0.5">
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                          <span className="text-xs font-semibold text-green-700">✓</span>
                        </div>
                      </div>
                      <div className="flex-1">
                        <h5 className="font-medium text-gray-800 text-sm mb-1">{role}</h5>
                        <p className="text-xs text-gray-600">{descriptions[role] || 'Business responsibility role'}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Examples Section */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-gray-800 flex items-center gap-2">
              <span className="text-lg">💡</span>
              <span>Real-World Examples</span>
            </h4>
            
            <div className="space-y-3">
              <div className="bg-white rounded p-3 border border-gray-200">
                <p className="text-sm font-medium text-gray-800 mb-1">Example 1: Multi-Role Manager</p>
                <p className="text-xs text-gray-600">
                  A user with roles: <strong>Manager</strong> + <strong>{availableOwnerRoles[0] || 'Owner Role'}</strong> + <strong>{availableOwnerRoles[availableOwnerRoles.length > 1 ? 1 : 0] || 'Other Role'}</strong>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  → Can manage users (Manager) AND is responsible for {availableOwnerRoles[0]?.toLowerCase() || 'primary'} AND {availableOwnerRoles[availableOwnerRoles.length > 1 ? 1 : 0]?.toLowerCase() || 'secondary'} compliance
                </p>
              </div>

              <div className="bg-white rounded p-3 border border-gray-200">
                <p className="text-sm font-medium text-gray-800 mb-1">Example 2: Compliance-Focused Admin</p>
                <p className="text-xs text-gray-600">
                  A user with roles: <strong>Admin</strong> + <strong>{availableOwnerRoles.find(r => r.includes('Compliance') || r.includes('Officer')) || availableOwnerRoles[0] || 'Owner Role'}</strong>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  → Has full system access (Admin) AND is responsible for all {availableOwnerRoles.find(r => r.includes('Compliance') || r.includes('Officer'))?.toLowerCase() || availableOwnerRoles[0]?.toLowerCase() || 'compliance'} tasks
                </p>
              </div>

              <div className="bg-white rounded p-3 border border-gray-200">
                <p className="text-sm font-medium text-gray-800 mb-1">Example 3: Functional Lead</p>
                <p className="text-xs text-gray-600">
                  A user with roles: <strong>User</strong> + <strong>{availableOwnerRoles[0] || 'Owner Role'}</strong>
                </p>
                <p className="text-xs text-gray-600 mt-1">
                  → Basic access (User) but responsible for all {availableOwnerRoles[0]?.toLowerCase() || 'functional'} related compliance and operations
                </p>
              </div>
            </div>
          </div>

          {/* Task Visibility Section */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-3">
            <h4 className="font-semibold text-yellow-900 mb-2 flex items-center gap-2">
              <span className="text-lg">📋</span>
              <span>Task Visibility & Completion</span>
            </h4>
            <div className="space-y-3 text-sm text-yellow-800">
              <div className="bg-white rounded p-3 border border-yellow-200">
                <p className="font-medium mb-1">Admin & Manager System Roles:</p>
                <p className="text-xs">
                  Users with <strong>Admin</strong> or <strong>Manager</strong> system roles can see and complete/review <strong>ALL tasks</strong> in their assigned locations, regardless of which owner role the task is assigned to. This allows them to oversee and manage all compliance activities.
                </p>
              </div>
              
              <div className="bg-white rounded p-3 border border-yellow-200">
                <p className="font-medium mb-1">User System Role:</p>
                <p className="text-xs">
                  Users with only the <strong>User</strong> system role can only see and complete tasks that match their <strong>owner roles</strong>. They cannot see or complete tasks assigned to other owner roles, even if those tasks are at their assigned locations.
                </p>
              </div>
              
              <div className="bg-white rounded p-3 border border-yellow-200">
                <p className="font-medium mb-1">Example:</p>
                <p className="text-xs">
                  If a task is assigned to <strong>{availableOwnerRoles[0] || 'Owner Role'}</strong> owner role:
                </p>
                <ul className="text-xs mt-1 ml-4 list-disc space-y-1">
                  <li>A user with <strong>User + {availableOwnerRoles[0] || 'Owner Role'}</strong> roles can see and complete it</li>
                  <li>A user with <strong>User + {availableOwnerRoles[availableOwnerRoles.length > 1 ? 1 : 0] || 'Other Role'}</strong> roles cannot see it</li>
                  <li>A user with <strong>Manager + {availableOwnerRoles[availableOwnerRoles.length > 1 ? 1 : 0] || 'Other Role'}</strong> roles can see and complete it (because Manager system role bypasses owner role restrictions)</li>
                  <li>A user with <strong>Admin + {availableOwnerRoles[availableOwnerRoles.length > 1 ? 1 : 0] || 'Other Role'}</strong> roles can see and complete it (because Admin system role bypasses owner role restrictions)</li>
                </ul>
              </div>
            </div>
          </div>

          {/* How It Works */}
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-indigo-900 mb-2">How It Works Together</h4>
            <div className="space-y-2 text-sm text-indigo-800">
              <p>
                <strong>System Roles</strong> determine <strong>what you can do</strong> in the system (access features, manage users, etc.) and <strong>task visibility</strong> (Admin/Manager see all tasks, User only sees tasks matching their owner roles).
              </p>
              <p>
                <strong>Owner Roles</strong> determine <strong>what you're responsible for</strong> (which compliance tasks, which locations, etc.) and which tasks you can see if you're a regular User.
              </p>
              <p className="mt-2 font-medium">
                You can select multiple roles of both types to give users the right combination of access and responsibilities!
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full btn-primary px-4 py-2"
          >
            Got it!
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleHelpModal;

